import { useQuery } from '@tanstack/react-query';
import { classes } from 'html-classes';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { company } from '~/company/Company';
import Icon from '~/components/Icon/Icon';
import geocoderRequest from '~/components/ModalDeliveryAddress/geocoderRequest';
import { useGlobal } from '~/hooks/useGlobal';

import styles from './AddressAutocomplete.module.scss';

interface Prediction {
  description: string;
  place_id: string;
}

interface AutocompleteResponse {
  data: {
    predictions: Prediction[];
  };
}

interface AddressAutocompleteProps {
  searchText: string;
  onSelect: (placeId: string, address: string) => void;
  searchSessionToken?: string;
  onLoadingStateChange?: (isLoading: boolean) => void;
  showSelectOnMap?: boolean;
  rowClassName?: string;
}

const AddressAutocomplete = ({
  searchText,
  onSelect,
  searchSessionToken,
  onLoadingStateChange,
  showSelectOnMap = true,
  rowClassName,
}: AddressAutocompleteProps) => {
  const { t } = useTranslation();
  const { isMobile } = useGlobal();
  const {
    data: { predictions },
    isFetching,
  } = useQuery({
    refetchOnWindowFocus: false,
    enabled: !!searchSessionToken && !!searchText,
    queryKey: ['autocomplete-search', searchText, searchSessionToken],
    initialData: {
      predictions: [],
    },
    queryFn: () =>
      geocoderRequest
        .post<AutocompleteResponse>('/autocomplete', {
          countryCode: company.config.countryCode || '',
          input: searchText,
          sessionToken: searchSessionToken,
        })
        .then(({ data: { data } }) => data),
  });

  useEffect(() => {
    onLoadingStateChange?.(isFetching);
  }, [isFetching]);

  return (
    <div className={styles.container}>
      {isMobile && showSelectOnMap && (
        <div
          data-select-item="autocompelete-result-select-on-map"
          className={styles.elementContainer}
          onClick={() => onSelect('')}
        >
          <div className={classes([styles.element, styles.goBack])}>
            <Icon type="geolocation" size={24} />
            {t('selectOnMap')}
          </div>
        </div>
      )}
      {predictions.map(({ description, place_id: placeId }) => (
        <div
          data-select-item={`autocompelete-result-${placeId}`}
          key={placeId}
          className={styles.elementContainer}
          onClick={() => onSelect(placeId, description)}
        >
          <div className={classes([styles.element, rowClassName])}>
            {description}
          </div>
        </div>
      ))}
    </div>
  );
};

export default AddressAutocomplete;
